import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Flame, Award, Heart, Sparkles, TreeDeciduous } from "lucide-react";

const TREE = "https://images.unsplash.com/photo-1749138197883-79537895b020";

const ACHIEVEMENTS = {
  first_step: { label: "First Step", desc: "You began your journey", icon: Sparkles },
  week_streak: { label: "Seven Days", desc: "A full week of showing up", icon: Flame },
  month_streak: { label: "Thirty Days", desc: "A month of consistency", icon: Award },
  growing_strong: { label: "Growing Strong", desc: "100 growth points", icon: TreeDeciduous },
};

export default function Growth() {
  const [g, setG] = useState({});
  useEffect(() => { api.get("/gamification").then(({ data }) => setG(data || {})).catch(() => {}); }, []);

  const points = g.growth_points || 0;
  const level = Math.floor(points / 100) + 1;
  const progressInLevel = points % 100;

  return (
    <div>
      <h1 className="font-serif text-3xl font-semibold sm:text-4xl">Your growth</h1>
      <p className="mt-2 max-w-lg text-muted-foreground">Gentle motivation only — no streaks to guilt you, just a quiet reflection of your care.</p>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                    className="relative overflow-hidden rounded-3xl border border-border/60 lg:col-span-2 ambient-shadow">
          <img src={TREE} alt="A bonsai growing in soft light" className="h-72 w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-0 p-8 text-white">
            <div className="flex items-center gap-2"><TreeDeciduous className="h-5 w-5" strokeWidth={1.5} /><span className="text-sm">Level {level} · Growth tree</span></div>
            <p className="mt-2 font-serif text-3xl">Every check-in helps you grow</p>
            <div className="mt-4 max-w-xs">
              <Progress value={progressInLevel} className="h-2 bg-white/20" />
              <p className="mt-1.5 text-xs text-white/80">{progressInLevel}/100 to level {level + 1}</p>
            </div>
          </div>
        </motion.div>

        <div className="space-y-4">
          {[
            { icon: Flame, label: "Current streak", value: `${g.streak || 0} days`, tint: "text-orange-500 bg-orange-500/10" },
            { icon: Award, label: "Best streak", value: `${g.best_streak || 0} days`, tint: "text-primary bg-primary/10" },
            { icon: Heart, label: "Kindness score", value: g.kindness_score || 0, tint: "text-destructive bg-destructive/10" },
          ].map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: i * 0.08 }}
                        className="ambient-shadow flex items-center gap-4 rounded-3xl border border-border/60 bg-card p-5">
              <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${s.tint}`}><s.icon className="h-5 w-5" strokeWidth={1.5} /></div>
              <div><div className="font-serif text-2xl">{s.value}</div><div className="text-sm text-muted-foreground">{s.label}</div></div>
            </motion.div>
          ))}
        </div>
      </div>

      <h2 className="mt-10 font-serif text-2xl">Milestones</h2>
      <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
        {Object.entries(ACHIEVEMENTS).map(([key, a], i) => {
          const earned = (g.achievements || []).includes(key);
          return (
            <motion.div key={key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.06 }}
                        className={`rounded-3xl border p-5 text-center ${earned ? "border-primary/40 bg-primary/5" : "border-border/60 bg-card opacity-50"}`}
                        data-testid={`achievement-${key}`}>
              <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${earned ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
                <a.icon className="h-6 w-6" strokeWidth={1.5} />
              </div>
              <p className="mt-3 font-medium text-sm">{a.label}</p>
              <p className="text-xs text-muted-foreground">{a.desc}</p>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
